import * as React from "react";
import * as S from "@styled";
import * as ProcessListActions from "processList/actions/ProcessListActions";
import * as TemplateActions from "processList/actions/TemplateActions";
import { State } from "processList/model/State";
import { DispatchType, Utilities } from "shared/utilities";
import { connect } from "react-redux";
import CustomModal from "shared/components/CustomModal";
import styled from "styled-components";
import colors from "shared/styles/colors";
import TagsInput from "shared/components/TagsInput";
import { Option } from "shared/model/Option";
import { Process } from "processList/model/Process";
import { ProcessVariableValueEditor } from "processEditor/components/ProcessVariablesPanel";
import { getErrorsOfType } from "../../ProcessVariablesEditorModal";
import { ProcessVarValidationErrorType, validateProcessVariableValue } from "processEditor/actions/ValidateProcessVariables";
import { getInitialProcessVariables } from "processTemplates/components/modals/CreateModal";
import { getProcessVariableValue } from "processEditor/reducers/ProcessVariablesReducer";
import { ProcessVariableEdit } from "processList/model/ProcessVariableEdit";
import { TemplateRequestBody } from "processList/model/Template";
import { ProcessTemplate } from "processTemplates/model/ProcessTemplate";
import { Checkbox } from "shared/components/Checkbox";


const CreateTemplateFromProcessDialog: React.FC<CreateTemplateFromProcessDialogProps> = ({
    processId,
    existingTemplate,
    processes,
    onCreateTemplate,
    onUpdateTemplate,
    onCloseCreateTemplateFromProcessDialog
}) => {
    const [currentPhase, setCurrentPhase] = React.useState(CreateTemplatePhase.CONFIGTEMPLATE);

    const [templateName, setTemplateName] = React.useState<string>();
    const [templateMajorVersion, setTemplateMajorVersion] = React.useState<number>();
    const [templateMinorVersion, setTemplateMinorVersion] = React.useState<number>();
    const [templateDescription, setTemplateDescription] = React.useState<string>();
    const [templateTags, setTemplateTags] = React.useState<string[]>();
    const [linkProcessToCreatedTemplate, setLinkProcessToCreatedTemplate] = React.useState<boolean>(true);

    const [processVariables, setProcessVariables] = React.useState<ProcessVariableEdit[]>();
    const [processTags, setProcessTags] = React.useState<string[]>();

    const [availableTags, setAvailableTags] = React.useState<string[]>([]);

    React.useEffect(() => {
        const fetchAvailableTags = async () => {
            try {
                const fetchedAvailableTags = await Utilities.getFetch<string[]>("api/tag");
                setAvailableTags(fetchedAvailableTags);
            } catch (e) {
                console.error(e);
                Utilities.toastError(e);
            }
        }
        fetchAvailableTags();
    }, []);

    React.useEffect(() => {
        if (processId) {
            const processInfo = processes.find((p) => p.id === processId);
            const processVariables = getInitialProcessVariables(processInfo?.processVariables);
            setTemplateName(existingTemplate?.name || processInfo?.name);
            setTemplateMajorVersion(existingTemplate?.majorVersion || processInfo?.majorVersion || 0);
            setTemplateMinorVersion(existingTemplate?.minorVersion || processInfo?.minorVersion || 1);
            setTemplateDescription(existingTemplate?.description || processInfo?.description);
            setTemplateTags(existingTemplate?.tags);
            setProcessVariables(processVariables);
            setProcessTags(processInfo?.tags);
        } else {
            setCurrentPhase(CreateTemplatePhase.CONFIGTEMPLATE);
            setTemplateName(undefined);
            setTemplateMajorVersion(undefined);
            setTemplateMinorVersion(undefined);
            setTemplateDescription(undefined);
            setTemplateTags(undefined);
            setProcessVariables(undefined);
            setProcessTags(undefined);
        }
    }, [processes, processId])

    if (!processId) {
        return null;
    }

    const getTitle = () => existingTemplate ? 'Update template' : 'Create new Template';

    const handleContinue = () => {
        if (templateName !== undefined && templateMajorVersion !== undefined && templateMinorVersion !== undefined) {
            setCurrentPhase(CreateTemplatePhase.CONFIGDEFAULTVALUES);
        } else {
            Utilities.toastError("Required fields are missing");
        }
    }

    const handleChangeTemplateTags = (newValues: Option[]) => {
        setTemplateTags(newValues.map((val) => val.value));
    }

    const handleProcessVariableChange = (id: number, newValue: string) => {
        if (!processVariables) {
            return;
        }

        const changedVariableIdx = processVariables.findIndex((pv) => pv.id === id);
        if (changedVariableIdx >= 0) {
            const changedVariable = processVariables[changedVariableIdx];
            changedVariable.displayValue = newValue;
            changedVariable.value = getProcessVariableValue(newValue, changedVariable.mode);

            const allowEmpty = true;
            const allowMissingEnvironmentVariable = true;
            changedVariable.validationErrors = validateProcessVariableValue(changedVariable, [], allowEmpty, allowMissingEnvironmentVariable);

            const changedVariables = [...processVariables];
            changedVariables[changedVariableIdx] = changedVariable;
            setProcessVariables(changedVariables);
        }
    }

    const handleChangeProcessTags = (newValues: Option[]) => {
        setProcessTags(newValues.map((val) => val.value));
    }

    const getRequestBody = (): TemplateRequestBody => ({
        processId,
        name: templateName,
        guid: existingTemplate?.guid,
        description: templateDescription,
        templateMajorVersion,
        templateMinorVersion,
        templateTags,
        processTags,
        processVariables
    });

    const handleCreateTemplate = () => {
        const requestBody = getRequestBody();
        requestBody.linkProcessToCreatedTemplate = linkProcessToCreatedTemplate;
        onCreateTemplate(requestBody);
    };

    const handleUpdateTemplate = () => onUpdateTemplate(getRequestBody());

    const handleClose = () => onCloseCreateTemplateFromProcessDialog();

    const createAndUpdateDisable = processVariables?.some(p => p.validationErrors.length > 0);

    return (
        <CustomModal
            modalTitle={<h2>{getTitle()}</h2>}
            label="create-template-from-process-dialog"
            show={!!processId}
            onClose={handleClose}
            shouldCloseOnOverlayClick={true}
            size="medium"
        >
            <ProcessConfigurationPhaseDecoration currentPhase={currentPhase} />

            <form
                id="create-template-form"
                onSubmit={(e) => e.preventDefault()}
            >
                {currentPhase === CreateTemplatePhase.CONFIGTEMPLATE && (
                    <>
                        <S.FormGroup>
                            <S.Label>
                                Template name
                            </S.Label>
                            <S.Input
                                id="template-name-input"
                                type="text"
                                required
                                maxLength={512}
                                placeholder="Template name"
                                value={templateName ?? ''}
                                onChange={(e) => setTemplateName(e.currentTarget.value)}
                                style={{ width: '100%' }}
                            />
                        </S.FormGroup>
                        <S.FormGroup style={{ display: "grid", gridTemplateColumns: "max-content max-content", gap: "2em" }}>
                            <div>
                                <S.Label>
                                    Major version
                                </S.Label>
                                <S.Input
                                    id="template-major-version-input"
                                    type="number"
                                    min={0}
                                    required
                                    value={templateMajorVersion ?? ''}
                                    onChange={(e) => setTemplateMajorVersion(parseInt(e.currentTarget.value))}
                                />
                            </div>
                            <div>
                                <S.Label>
                                    Minor version
                                </S.Label>
                                <S.Input
                                    id="template-minor-version-input"
                                    type="number"
                                    min={0}
                                    required
                                    value={templateMinorVersion ?? ''}
                                    onChange={(e) => setTemplateMinorVersion(parseInt(e.currentTarget.value))}
                                />
                            </div>
                        </S.FormGroup>
                        <S.FormGroup>
                            <S.Label>
                                Template description
                            </S.Label>

                            <S.TextArea
                                id="template-description-input"
                                value={templateDescription ?? ''}
                                onChange={(e) => setTemplateDescription(e.currentTarget.value)}
                            />
                        </S.FormGroup>

                        <TagsInput label="Template-tags" availableTags={availableTags} selectedTags={templateTags} handleChangeTags={handleChangeTemplateTags} />
                        {!existingTemplate && (
                            <S.FormGroup>
                                <S.Label style={{ display: "flex" }}>
                                    <Checkbox
                                        id="link-process-to-created-template-checkbox"
                                        checked={linkProcessToCreatedTemplate}
                                        onChange={() => setLinkProcessToCreatedTemplate(!linkProcessToCreatedTemplate)}
                                    />
                                    Link Process to created Template
                                </S.Label>
                            </S.FormGroup>
                        )}
                    </>
                )}

                {currentPhase === CreateTemplatePhase.CONFIGDEFAULTVALUES && (
                    <>
                        {processVariables !== undefined && processVariables.length > 0 && (
                            <S.FormGroup>
                                <p>Configure placeholder variables for Template</p>
                                {processVariables?.map((pv, i) => {
                                    const valueErrors = getErrorsOfType(pv, ProcessVarValidationErrorType.Value);
                                    const referenceErrors = getErrorsOfType(pv, ProcessVarValidationErrorType.Reference);

                                    return (
                                        <S.FormGroup key={i}>
                                            <ProcessVariableValueEditor
                                                label={pv.name}
                                                processVariable={pv}
                                                valueErrors={valueErrors}
                                                referenceError={referenceErrors.length ? referenceErrors[0] : undefined}
                                                updateVariableValue={handleProcessVariableChange}
                                                disableEdit={false}
                                                onlyEditValue={true}
                                                showModeSelector={true}
                                                hideDescription={pv.description?.length === 0}
                                            />
                                        </S.FormGroup>
                                    )
                                })}
                                <hr />
                            </S.FormGroup>
                        )}

                        <TagsInput label="Process-tags" availableTags={availableTags} selectedTags={processTags} handleChangeTags={handleChangeProcessTags} />
                    </>
                )}
            </form>

            <S.FormGroup style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                <S.Button variant={S.ButtonType.bordered} onClick={handleClose}>
                    {FRENDS.Localizations.Cancel}
                </S.Button>

                <S.ButtonGroup $justifyContentEnd={true} gap={10}>
                    {currentPhase == CreateTemplatePhase.CONFIGDEFAULTVALUES && (
                        <S.Button variant={S.ButtonType.bordered} onClick={() => setCurrentPhase(CreateTemplatePhase.CONFIGTEMPLATE)}>
                            Back
                        </S.Button>
                    )}

                    {currentPhase === CreateTemplatePhase.CONFIGTEMPLATE && (
                        <S.Button onClick={handleContinue}>Continue</S.Button>
                    )}

                    {currentPhase === CreateTemplatePhase.CONFIGDEFAULTVALUES && !existingTemplate && (
                        <S.Button
                            onClick={handleCreateTemplate}
                            disabled={createAndUpdateDisable}
                        >
                            Create
                        </S.Button>
                    )}

                    {currentPhase === CreateTemplatePhase.CONFIGDEFAULTVALUES && existingTemplate && (
                        <S.Button
                            onClick={handleUpdateTemplate}
                            disabled={createAndUpdateDisable}
                        >
                            Update
                        </S.Button>
                    )}
                </S.ButtonGroup>
            </S.FormGroup>
        </CustomModal>
    )
}

const ProcessConfigurationPhaseDecoration: React.FC<IProcessConfigurationPhaseDecorationProps> = ({
    currentPhase
}) => {

    const getPhaseDecorationSpanClassName = (curPhase: CreateTemplatePhase, cutOffPhase: CreateTemplatePhase) => {
        if (cutOffPhase < curPhase) {
            return undefined;
        } else if (cutOffPhase === curPhase) {
            return 'bold';
        } else {
            return 'grey';
        }
    };

    return (
        <StyledProcessConfigurationPhaseDecoration style={{ gridTemplateColumns: "auto 1fr auto" }}>
            <span className={getPhaseDecorationSpanClassName(currentPhase, CreateTemplatePhase.CONFIGTEMPLATE)}>
                Configure Template
            </span>

            <div>
                &nbsp;
            </div>

            <span className={getPhaseDecorationSpanClassName(currentPhase, CreateTemplatePhase.CONFIGDEFAULTVALUES)}>
                Configure default values
            </span>
        </StyledProcessConfigurationPhaseDecoration>
    );
};

const StyledProcessConfigurationPhaseDecoration = styled.div`
    margin: 25px 0;
    display: grid;
    align-items: center;
    gap: 24px;

    * {
        font-size: 14px;
    }
    
    span.bold {
        font-family: 'Gilroy-Bold';
    }

    span.grey {
        color: ${colors.grey_1};
    }

    div {
        height: 1px;
        background-color: ${colors.grey_2};
    }
`;

enum CreateTemplatePhase {
    CONFIGTEMPLATE,
    CONFIGDEFAULTVALUES,
}

interface IProcessConfigurationPhaseDecorationProps {
    currentPhase: CreateTemplatePhase;
}

interface CreateTemplateFromProcessDialogStateProps {
    processId?: number;
    existingTemplate?: ProcessTemplate;
    processes: Process[];
}

interface CreateTemplateFromProcessDialogDispatchProps {
    onCreateTemplate: (template: TemplateRequestBody) => void;
    onUpdateTemplate: (template: TemplateRequestBody) => void;
    onCloseCreateTemplateFromProcessDialog: () => void;
}

type CreateTemplateFromProcessDialogProps = CreateTemplateFromProcessDialogStateProps & CreateTemplateFromProcessDialogDispatchProps;

const mapStateToProps = (state: State): CreateTemplateFromProcessDialogStateProps => ({
    processId: state.listState.createTemplateFromProcessModal?.processId,
    existingTemplate: state.listState.createTemplateFromProcessModal?.existingTemplate,
    processes: state.processes
});

const mapDispatchToProps = (dispatch: DispatchType<State>): CreateTemplateFromProcessDialogDispatchProps => ({
    onCreateTemplate: (template: TemplateRequestBody) => dispatch(TemplateActions.createTemplate(template)),
    onUpdateTemplate: (template: TemplateRequestBody) => dispatch(TemplateActions.updateTemplate(template)),
    onCloseCreateTemplateFromProcessDialog: () => dispatch(ProcessListActions.setCreateTemplateFromProcessModal(null))
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateTemplateFromProcessDialog);
